import { LocaleMessageObject }            from 'vue-i18n';
import memoize                            from 'lodash-es/memoize';
import { saveAsPng }                      from 'save-html-as-image';
import useGlobalTranslations              from '@/hooks/use-global-translations';
import { ChartPosition }                  from '@/components/chart/helpers/chart-variables';
import iconAreaSelect                     from '@/components/icon-image/icon-area-select.png';
import iconRestore                        from '@/components/icon-image/icon-restore.png';
import iconRefresh                        from '@/components/icon-image/icon-refresh.png';
import iconDownload                       from '@/components/icon-image/icon-download.png';
import { GlobalContexts, useGlobals }     from '@/hooks/use-globals';
import { ObjectWithStringKeys }           from '@/types';
import { getCSSVariable, rgbStringToHex } from '@/styles/variables.helpers';
import { computed }                       from '@vue/composition-api';

export interface ToolboxFeature {
  show: boolean;
  icon: string | ObjectWithStringKeys<string>;
  xAxisIndex?: number | number[];
  yAxisIndex?: number | number[];
  onclick?: () => void;
  title: string | { zoom: string; back: string };
}

export interface ChartToolbox {
  show?: boolean;
  emphasis?: {
    iconStyle: {
      opacity: number;
      textAlign: ChartPosition;
      textPosition: ChartPosition;
      color: string;
    };
  };
  orient?: string;
  bottom?: number;
  itemSize?: number;
  left?: number | string;
  itemGap?: number;
  showTitle?: boolean;
  feature?: ObjectWithStringKeys<ToolboxFeature>;
  height?: string;
}

const generateBasicToolboxConfiguration = (config: ChartToolbox): ChartToolbox => ({
  show: true,
  itemSize: 18,
  itemGap: 20,
  showTitle: true,
  emphasis: {
    iconStyle: {
      opacity: 0.5,
      textAlign: ChartPosition.CENTER,
      textPosition: ChartPosition.BOTTOM,
      color: rgbStringToHex(getCSSVariable('--primary-500')),
    },
  },
  ...config,
});
const createImgFromCanvas = (component: string): HTMLImageElement => {
  const image: HTMLImageElement = new Image();
  const sourceCanvas: HTMLCanvasElement | null = document.querySelector(`${component} canvas`);
  const sourceImageData: string | undefined = sourceCanvas?.toDataURL('image/png');
  image.width = sourceCanvas?.width ?? 0;
  image.height = sourceCanvas?.height ?? 0;
  image.src = sourceImageData ?? '';
  return image;
};
const saveToPng = async (component: string, filename: string, printDate = false): Promise<void> => {
  const customWrapper: HTMLElement = document.createElement('div');
  customWrapper.style.backgroundColor = '#FFF';
  const filtersClone: Node | undefined = document.querySelector('.c-selected-filters-panel__filters-wrapper')
    ?.cloneNode(true);
  const destinationImage: HTMLImageElement = createImgFromCanvas(component);

  customWrapper.appendChild(destinationImage);
  if (filtersClone) {
    filtersClone.removeChild(filtersClone.lastChild as ChildNode);
    customWrapper.appendChild(filtersClone);
  }

  document.body.style.overflow = 'hidden';
  document.body.appendChild(customWrapper);
  await saveAsPng(customWrapper, { filename, printDate });
  document.body.removeChild(customWrapper);
  document.body.style.overflow = 'visible';
};

export const useChartToolbox = memoize((chartHeight: number | null): ChartToolbox => {
  const { store, i18n }: GlobalContexts = useGlobals();
  const toolboxTranslation = useGlobalTranslations('chartSettings.toolbox') as LocaleMessageObject;
  const basicImagePath = 'image://';
  const chartName: string = i18n.t(`${store.state.route.name}.chart.name`).toString();
  const toolboxPositionLeft = computed((): string => (chartHeight && chartHeight < 540 ? '2%' : '4.5%'));

  return generateBasicToolboxConfiguration({
    height: '20%',
    orient: ChartPosition.VERTICAL,
    bottom: 55,
    left: toolboxPositionLeft.value,
    feature: {
      dataZoom: {
        show: true,
        xAxisIndex: [ 0 ],
        yAxisIndex: [ 0 ],
        icon: {
          zoom: `${basicImagePath}${iconAreaSelect}`,
          back: `${basicImagePath}${iconRestore}`,
        },
        title: {
          zoom: toolboxTranslation.sectionView.toString(),
          back: toolboxTranslation.defaultView.toString(),
        },
      },
      restore: {
        show: true,
        icon: `${basicImagePath}${iconRefresh}`,
        title: toolboxTranslation.refresh.toString(),
      },
      mySaveToPng: {
        show: true,
        icon: `${basicImagePath}${iconDownload}`,
        onclick: () => {
          saveToPng('.c-chart', chartName, true);
        },
        title: toolboxTranslation.download.toString(),
      },
    },
  });
});
